import { useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@/utils/toast';
import type { PurchaseSummaryItem } from '@/types/purchase';

/**
 * usePurchaseCelebration
 *
 * Fires a one-shot confetti burst + success toast when `?purchase=success` is
 * present in the URL.  After firing it removes the `purchase` param so that
 * a page refresh or re-render does NOT re-fire.
 *
 * Mount once inside DashboardLayout so it covers every authenticated route.
 *
 * Reduced-motion: if `prefers-reduced-motion: reduce` is set, the confetti
 * is skipped but the toast still shows.
 *
 * Double-fire guard: `firedRef` is a stable per-mount latch — the effect runs
 * exactly once regardless of re-renders, and `setSearchParams` (replace mode)
 * removes `purchase` from the URL so subsequent mounts on the same URL won't
 * find the param.
 */
export const usePurchaseCelebration = (): void => {
  const { t } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const firedRef = useRef(false);

  useEffect(() => {
    // Only fire once per mount, and only when the param is present
    if (firedRef.current) return;
    if (searchParams.get('purchase') !== 'success') return;
    // Native Shop returns with order/checkout IDs; Billing waits for activation
    // before showing its own success state.
    if (searchParams.has('order') || searchParams.has('checkout')) return;

    firedRef.current = true;

    // Read purchase summary from navigation state (set by WelcomePage redirect)
    const locationState = location.state as { purchaseSummary?: PurchaseSummaryItem[] } | null;
    const summary: PurchaseSummaryItem[] = locationState?.purchaseSummary ?? [];

    const tokensItem = summary.find((i): i is Extract<PurchaseSummaryItem, { kind: 'tokens' }> => i.kind === 'tokens');
    const planItem = summary.find((i): i is Extract<PurchaseSummaryItem, { kind: 'plan' }> => i.kind === 'plan');
    const hasWhitelabel = summary.some((i) => i.kind === 'whitelabel');

    // Pick the most specific toast message available.
    // White-label takes precedence — it is the primary purchase being celebrated
    // and the buyer has been redirected to the white-label settings page.
    let message: string;
    if (hasWhitelabel) {
      message = t('purchase_celebration_whitelabel');
    } else if (planItem && tokensItem) {
      message = t('purchase_celebration_combined', {
        plan: planItem.planName,
        count: tokensItem.amount,
        amount: tokensItem.amount.toLocaleString(),
      });
    } else if (tokensItem) {
      message = t('purchase_celebration_tokens', {
        count: tokensItem.amount,
        amount: tokensItem.amount.toLocaleString(),
      });
    } else if (planItem) {
      message = t('purchase_celebration_plan', { plan: planItem.planName });
    } else {
      // Empty summary or fetch failed — fall back to generic toast (unchanged)
      message = t('purchase_celebration_toast');
    }

    // Show success toast (always, regardless of motion preference)
    toast.success(message, { duration: 7000 });

    // Fire confetti unless user prefers reduced motion
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      // Dynamic import — code-split, not in main bundle
      import('canvas-confetti').then((module) => {
        const confetti = module.default;

        // Burst 1 — from center-left
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { x: 0.4, y: 0.6 },
          disableForReducedMotion: true,
        });

        // Burst 2 — from center-right, slight delay
        setTimeout(() => {
          confetti({
            particleCount: 60,
            spread: 55,
            origin: { x: 0.6, y: 0.55 },
            disableForReducedMotion: true,
          });
        }, 200);
      }).catch(() => {
        // Confetti is cosmetic — silently ignore load failures
      });
    }

    // Strip `purchase` from URL without triggering a navigation (replace mode)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('purchase');
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: run on mount only; searchParams captured via closure on first render
};
